<script setup lang="ts">
/**
 * CreateCampaignModal — 1:1 порт docs/mockups/modal-create-campaign.html
 * (inbox-178).
 *
 * Спека: docs/specs/2026-05-19-pages-behavior.md §5 «Кампании — список»,
 * Primary CTA «+ Создать кампанию» → modal:create-campaign · success →
 * unshift row in grid.
 *
 * Секции мокапа:
 *   1. Название (UiInput, required, maxlength 60)
 *   2. Описание (textarea, optional)
 *   3. Цвет тега — swatch-row из 8 hex-значений (CAMPAIGN_COLORS)
 *   4. Участники — UiDevCard (нет endpoint /campaigns/{id}/members)
 *   5. Toggle «Отслеживать бюджет» + budget input + period select —
 *      UiDevCard (CampaignCreate не принимает budget на бэке)
 *
 * POST /api/v1/campaigns через CampaignsApi.create({ name, description, color }).
 * Success → modals.emit('create-campaign', campaign) → modals.close().
 *
 * Открывается из CampaignsListView через row-action «+ Создать кампанию»:
 *   modals.open('create-campaign');
 *   modals.on('create-campaign', (c) => { /* unshift в grid * / });
 */

import { computed, ref, watch } from 'vue';
import { CampaignsApi, CAMPAIGN_COLORS, type Campaign, type CampaignCreate } from '@/api/campaigns';
import { ApiError } from '@/api/links';
import { useModalStore } from '@/stores/modal';
import UiModal from '@/components/ui/UiModal.vue';
import UiField from '@/components/ui/UiField.vue';
import UiInput from '@/components/ui/UiInput.vue';
import UiModalFooter from '@/components/ui/UiModalFooter.vue';
import UiDevCard from '@/components/ui/UiDevCard.vue';

const modals = useModalStore();

const name = ref('');
const description = ref('');
const color = ref<string>(CAMPAIGN_COLORS[0]!);
const trackBudget = ref(false);
const error = ref<string | null>(null);
const submitting = ref(false);

watch(
  () => modals.activeKey,
  (key) => {
    if (key !== 'create-campaign') return;
    name.value = '';
    description.value = '';
    color.value = CAMPAIGN_COLORS[0]!;
    trackBudget.value = false;
    error.value = null;
    submitting.value = false;
  },
);

const nameError = computed(() => {
  const v = name.value.trim();
  if (!v) return '';
  // inbox-240 — минимум 2 символа (название из 1 символа бесполезно
  // в списке кампаний и не отличает кампании в фильтрах ссылок).
  if (v.length < 2) return 'Минимум 2 символа';
  if (v.length > 60) return 'Не больше 60 символов';
  return '';
});
const canSubmit = computed(
  () => name.value.trim().length >= 2 && !nameError.value && !submitting.value,
);

async function submit(): Promise<void> {
  const trimmed = name.value.trim();
  if (!trimmed) {
    error.value = 'Введите название кампании';
    return;
  }
  if (nameError.value) {
    error.value = nameError.value;
    return;
  }
  error.value = null;
  submitting.value = true;
  modals.setPending(true);
  try {
    const body: CampaignCreate = {
      name: trimmed,
      description: description.value.trim() || null,
      color: color.value,
    };
    const created: Campaign = await CampaignsApi.create(body);
    modals.emit('create-campaign', created);
    modals.close();
  } catch (e) {
    if (e instanceof ApiError) {
      if (e.status === 401) error.value = 'Сессия истекла. Перезайдите.';
      else if (e.status === 403) error.value = 'Недостаточно прав для создания кампании.';
      else if (e.status === 409) error.value = 'Кампания с таким названием уже существует.';
      else if (e.status === 422) error.value = e.message || 'Невалидные данные.';
      else if (e.status === 429) error.value = 'Слишком много запросов. Подождите минуту.';
      else error.value = e.message || `Ошибка сервера (${e.status})`;
    } else {
      error.value = (e as Error).message || 'Сеть недоступна.';
    }
  } finally {
    submitting.value = false;
    modals.setPending(false);
  }
}

function cancel(): void {
  if (submitting.value) return;
  modals.close();
}
</script>

<template>
  <UiModal
    title="Создать кампанию"
    subtitle="Объедините группу ссылок под общим именем, цветом и бюджетом."
    size="sm"
    :disable-close="submitting"
    @close="cancel"
  >
    <form
      id="create-campaign-form"
      @submit.prevent="submit"
    >
      <UiField
        label="Название"
        hint="Видно команде в списке кампаний и в фильтрах ссылок."
        :error="nameError"
      >
        <UiInput
          v-model="name"
          type="text"
          placeholder="Q2 ретаргетинг"
          :disabled="submitting"
          required
        />
      </UiField>

      <UiField label="Описание">
        <textarea
          v-model="description"
          class="textarea"
          rows="3"
          placeholder="Цели, гипотезы, ответственные. Видно только команде."
          :disabled="submitting"
        />
      </UiField>

      <UiField label="Цвет тега">
        <div
          class="swatch-row"
          role="radiogroup"
          aria-label="Цвет"
        >
          <label
            v-for="c in CAMPAIGN_COLORS"
            :key="c"
            class="swatch"
            :style="{ '--c': c }"
            :title="c"
          >
            <input
              v-model="color"
              type="radio"
              name="campaign-color"
              :value="c"
              :disabled="submitting"
            >
            <span />
          </label>
        </div>
      </UiField>

      <UiDevCard
        compact
        feature="Участники кампании"
        message="Подключение участников появится когда добавим /api/v1/campaigns/{id}/members."
      >
        После создания кампании добавить участников можно будет на странице кампании.
      </UiDevCard>

      <div class="toggle-row">
        <label class="toggle">
          <input
            v-model="trackBudget"
            type="checkbox"
            :disabled="submitting"
          >
          <span class="toggle-slider" />
        </label>
        <div class="toggle-text">
          <div class="toggle-title">
            Отслеживать бюджет
          </div>
          <div class="toggle-desc">
            Сравнение фактических конверсий с лимитом — алерт за 10% до исчерпания.
          </div>
        </div>
      </div>

      <UiDevCard
        v-if="trackBudget"
        compact
        feature="Бюджет кампании"
        message="Поле budget пока не принимается бэкендом. Сейчас можно отслеживать только название и цвет."
      >
        В следующих спринтах появятся budget + period в CampaignCreate API.
      </UiDevCard>

      <p
        v-if="error"
        class="form-msg err"
        role="alert"
      >
        {{ error }}
      </p>
    </form>

    <template #footer>
      <UiModalFooter
        save-type="submit"
        save-form="create-campaign-form"
        :save-disabled="!canSubmit"
        :save-loading="submitting"
        :cancel-disabled="submitting"
        @cancel="cancel"
      >
        <template #save>Создать кампанию</template>
      </UiModalFooter>
    </template>
  </UiModal>
</template>

<style scoped>
.textarea {
  width: 100%;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  padding: 10px 14px;
  background: var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--fg);
  font-size: 13.5px;
  line-height: 1.5;
}
.textarea:focus {
  outline: none;
  border-color: var(--accent);
}
.textarea:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* Color swatches */
.swatch-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.swatch {
  position: relative;
  cursor: pointer;
  display: inline-flex;
}
.swatch input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.swatch span {
  display: block;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--c);
  box-shadow: inset 0 0 0 2px var(--bg-elev), 0 0 0 1px var(--border);
  transition: transform 0.1s;
}
.swatch:hover span {
  transform: scale(1.08);
}
.swatch input:checked + span {
  box-shadow: inset 0 0 0 3px var(--bg-elev), 0 0 0 2px var(--c);
}
.swatch input:disabled + span {
  opacity: 0.55;
  cursor: not-allowed;
}

/* Toggle row */
.toggle-row {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  padding-top: 4px;
  margin-bottom: 14px;
}
.toggle {
  position: relative;
  width: 36px;
  height: 20px;
  flex-shrink: 0;
}
.toggle input {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
}
.toggle-slider {
  position: absolute;
  inset: 0;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 100px;
  transition: 0.15s;
  cursor: pointer;
}
.toggle-slider::before {
  content: '';
  position: absolute;
  width: 14px;
  height: 14px;
  left: 2px;
  top: 2px;
  background: var(--fg);
  border-radius: 50%;
  transition: 0.15s;
}
.toggle input:checked + .toggle-slider {
  background: var(--accent);
  border-color: var(--accent);
}
.toggle input:checked + .toggle-slider::before {
  transform: translateX(16px);
  background: #fff;
}
.toggle input:disabled + .toggle-slider {
  opacity: 0.55;
  cursor: not-allowed;
}
.toggle-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.toggle-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--fg);
}
.toggle-desc {
  font-size: 12px;
  color: var(--fg-muted);
  line-height: 1.4;
}

/* Form message */
.form-msg {
  padding: 8px 12px;
  font-size: 12.5px;
  border-radius: 6px;
  margin-top: 8px;
}
.form-msg.err {
  color: var(--danger);
  background: var(--danger-soft);
}
</style>
